import { useEffect, useRef, useState } from 'react';

export const ONBOARDING_ANALYSIS_SETTLE_MS = 900;

interface UseOnboardingAnalysisSettleOptions {
  /** When true, show the summary immediately (selections existed when the step opened). */
  skipInitialAnimation?: boolean;
  settleMs?: number;
  /**
   * When true, replay the analyzing progress bar whenever the selection changes
   * after the initial settle (e.g. toggling another investing style).
   */
  replayOnChange?: boolean;
}

/**
 * Drives the analyzing → summary transition for onboarding insight panels.
 * Skips the progress animation when `skipInitialAnimation` is set because
 * selections were already present when the step was opened.
 */
export function useOnboardingAnalysisSettle(
  selectionKey: string,
  {
    skipInitialAnimation = false,
    settleMs = ONBOARDING_ANALYSIS_SETTLE_MS,
    replayOnChange = false,
  }: UseOnboardingAnalysisSettleOptions = {},
): boolean {
  const [isSettled, setIsSettled] = useState(() => skipInitialAnimation && selectionKey.length > 0);
  const hasSettledOnceRef = useRef(skipInitialAnimation && selectionKey.length > 0);
  const previousKeyRef = useRef(selectionKey);
  const skipInitialRef = useRef(skipInitialAnimation);

  useEffect(() => {
    if (selectionKey.length === 0) {
      hasSettledOnceRef.current = false;
      previousKeyRef.current = selectionKey;
      setIsSettled(false);
      return;
    }

    const keyChanged = previousKeyRef.current !== selectionKey;
    previousKeyRef.current = selectionKey;

    // First paint with an existing selection (reopen / back-nav): stay settled.
    if (skipInitialRef.current && !keyChanged && hasSettledOnceRef.current) {
      return;
    }

    if (hasSettledOnceRef.current && !(replayOnChange && keyChanged)) {
      return;
    }

    setIsSettled(false);
    const timer = window.setTimeout(() => {
      hasSettledOnceRef.current = true;
      setIsSettled(true);
    }, settleMs);
    return () => window.clearTimeout(timer);
  }, [selectionKey, settleMs, replayOnChange]);

  return isSettled;
}
